import { Link, Navigate, useParams } from "react-router-dom";
import { PageError } from "../components/PageError";
import { useFinding, useRecipe } from "../lib/api/queries";
import { useQueryView } from "../lib/loading/useQueryView";
import { hasContextualProjectNav } from "../lib/nav";
import { DetalleObservacionPostventa } from "./DetalleObservacionPostventa";

// /observaciones/:id — the observation thread's detail, by capability and
// never by recipe.slug (§0.4(6)).
//
// Since ADR-0134 (Fase 4) every recipe with an observation lifecycle uses
// the same thread screen (DetalleObservacionPostventa); the delegation
// detail (DetalleObservacion.tsx) is retired. What still differs is the
// ADDRESS: a vertical that frames its nav around the project (panel
// postventa v2, `hasContextualProjectNav`) has a canonical
// /proyectos/:id/observaciones/:obsId, so this legacy URL forwards there
// once the finding's project is known. Facility has no project nav — its
// detail lives here.
//
// While the recipe or the finding is still on its way the detail screen
// itself is mounted: it draws the page header and the thread skeleton
// (ADR-0166) until both land, so the route never renders a blank. The
// forward and the populated detail need the same two reads, so they are
// decided in the same render — a project-framed observation never paints
// in place before it moves. A failed recipe ends here, in an error with
// Reintentar: without it neither the address nor the screen can resolve.
export function ObservacionDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const recipeQuery = useRecipe();
  const recipeView = useQueryView(recipeQuery);
  const recipe = recipeQuery.data;
  // The detail screen reads this same query (same key): no second
  // request. Its failure is the screen's to show (not-found, error).
  const { data: finding } = useFinding(id);

  if (recipeView.kind === "error") {
    return (
      <div className="page">
        <div className="page-header" style={{ marginBottom: 20 }}>
          <div className="page-header__lead">
            <div className="page-header__eyebrow">
              <Link to="/observaciones">Observaciones</Link>
            </div>
          </div>
        </div>
        {recipeView.offline ? (
          <PageError
            title="Sin conexión"
            description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
            onRetry={recipeView.retry}
          />
        ) : (
          <PageError
            title="No pudimos cargar la observación"
            description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
            onRetry={recipeView.retry}
          />
        )}
      </div>
    );
  }

  const groupId = finding?.asset.assetGroupId ?? null;
  if (recipe && hasContextualProjectNav(recipe) && groupId !== null && id) {
    return <Navigate to={`/proyectos/${groupId}/observaciones/${id}`} replace />;
  }
  // Facility, a finding with no project, a failed read, or either read
  // still loading: the screen in place owns its skeleton and its errors.
  return <DetalleObservacionPostventa />;
}
